import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { api, formatApiError } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Search, User, Phone, Mail, UserX, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
  nom: "", prenom: "", telephone: "", adresse: "", email: "", notes: "",
};

export default function ClientListPage() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [dormant, setDormant] = useState([]);
  const [showDormant, setShowDormant] = useState(false);
  const canReviewDormant = hasPerm(user, "client", "delete") || user?.is_admin;

  const load = async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: q ? { q } : {} });
      setClients(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // RGPD (art. 5-1-e) : repère les fiches client sans activité depuis
  // longtemps — ne fait que PROPOSER des candidats, jamais d'anonymisation
  // automatique (voir compliance_utils.list_dormant_client_candidates et le
  // bouton "Anonymiser" déjà présent sur la fiche client).
  useEffect(() => {
    if (!canReviewDormant) return;
    api.get("/clients/rgpd/dormant-candidates").then(({ data }) => setDormant(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReviewDormant]);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  if (!hasPerm(user, "client", "view")) return <Navigate to="/" replace />;

  const openCreate = () => { setForm(EMPTY_FORM); setOpen(true); };

  const save = async () => {
    if (!form.nom.trim()) { toast.error("Le nom est obligatoire"); return; }
    try {
      const { data } = await api.post("/clients", form);
      toast.success("Fiche client créée");
      setOpen(false);
      load(search);
      window.location.href = `/clients/${data.id}`;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Layout title="Clients">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="relative w-full sm:max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="client-search-input"
            placeholder="Rechercher par nom, prénom, tél, email, n° de fiche/ticket..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        {hasPerm(user, "client", "create") && (
          <Button data-testid="client-add-button" className="gap-2 shrink-0 w-full sm:w-auto" onClick={openCreate}>
            <Plus size={16} /> Nouveau client
          </Button>
        )}
      </div>

      {canReviewDormant && dormant.length > 0 && (
        <div className="rounded-xl border border-amber-600/30 bg-amber-600/5 p-3 mb-4" data-testid="dormant-clients-panel">
          <button
            type="button"
            className="flex items-center justify-between w-full text-sm font-medium"
            onClick={() => setShowDormant((v) => !v)}
          >
            <span className="flex items-center gap-1.5"><UserX size={15} /> {dormant.length} fiche(s) client sans activité depuis plus de 3 ans (RGPD)</span>
            {showDormant ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {showDormant && (
            <div className="mt-2 space-y-1">
              <p className="text-xs text-muted-foreground mb-1.5">
                Candidates à examiner pour une éventuelle anonymisation — rien n'est jamais anonymisé automatiquement,
                cette liste ne fait que proposer.
              </p>
              {dormant.map((c) => (
                <Link key={c.client_id} to={`/clients/${c.client_id}`} className="flex justify-between text-sm py-1 hover:underline">
                  <span>{c.prenom} {c.nom}</span>
                  <span className="text-xs text-muted-foreground">
                    {c.last_activity ? `Dernière activité le ${c.last_activity.slice(0, 10)}` : `Fiche créée le ${c.reference_date.slice(0, 10)}, jamais utilisée`}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="client-list">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-secondary text-left">
            <tr>
              <th className="p-3">Nom</th>
              <th className="p-3">Téléphone</th>
              <th className="p-3">Email</th>
              <th className="p-3">Adresse</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id} className="border-t border-border" data-testid={`client-row-${c.id}`}>
                <td className="p-3 font-medium">{c.prenom} {c.nom}</td>
                <td className="p-3 text-muted-foreground">{c.telephone || "—"}</td>
                <td className="p-3 text-muted-foreground">{c.email || "—"}</td>
                <td className="p-3 text-muted-foreground truncate max-w-[220px]">{c.adresse || "—"}</td>
                <td className="p-3 text-right">
                  <Link to={`/clients/${c.id}`} data-testid={`client-open-${c.id}`} className="text-primary text-sm">
                    Ouvrir le dossier
                  </Link>
                </td>
              </tr>
            ))}
            {!loading && clients.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Aucun client trouvé.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="client-form-dialog">
          <DialogHeader><DialogTitle>Nouvelle fiche client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="relative">
                <User size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="client-form-nom" placeholder="Nom" className="pl-8" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} />
              </div>
              <Input data-testid="client-form-prenom" placeholder="Prénom" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} />
            </div>
            <div className="relative">
              <Phone size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="client-form-telephone" placeholder="Numéro de téléphone" className="pl-8" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} />
            </div>
            <div className="relative">
              <Mail size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="client-form-email" placeholder="Email" className="pl-8" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <Input data-testid="client-form-adresse" placeholder="Adresse postale" value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} />
          </div>
          <DialogFooter>
            <Button data-testid="client-form-save" onClick={save}>Créer la fiche</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
